"use client";

import ErrorBoundaryWrapper from "@/components/error/ErrorBoundaryWrapper";
import { AntdThemeProvider } from "@/components/providers/antd_theme";
import { TooltipProvider } from "@/components/ui/tooltips/tooltip";
import StyledComponentsRegistry from "@/utils/AntdRegistry";
import { FixGoogleTranslate } from "@/utils/FixGoogleTranslate";
import { AnalyticsProvider } from "@/utils/analytics-provider";
import {
  QueryClient,
  QueryClientProvider,
  isServer,
} from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/next/app";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function Providers({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  return (
    <AnalyticsProvider>
      <ErrorBoundaryWrapper>
        <QueryClientProvider client={queryClient}>
          <AntdThemeProvider>
            {/* initial delay of 500ms, switching between buttons with tooltip within 500ms is instant */}
            <TooltipProvider delayDuration={500} skipDelayDuration={500}>
              <FixGoogleTranslate>
                <StyledComponentsRegistry>
                  <NuqsAdapter>{children}</NuqsAdapter>
                </StyledComponentsRegistry>
              </FixGoogleTranslate>
            </TooltipProvider>
          </AntdThemeProvider>
        </QueryClientProvider>
      </ErrorBoundaryWrapper>
    </AnalyticsProvider>
  );
}
